import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, type GridSortModel, type GridColDef, type GridFilterModel } from '@mui/x-data-grid';
import type { GeoJsonFeature } from '../../utils/ogc';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import type { AugmentedSensorFeature } from '../../layers/sensor_locations';
import { Typography } from '@mui/material';
import { format } from 'date-fns';



export function SensorLegendTable({ features, currentTime, highlightedKey, onSelect }: { features: AugmentedSensorFeature[]; currentTime?: Date; highlightedKey?: string | null; onSelect?: (f: GeoJsonFeature) => void }) {
  const theme = useTheme();
//   const rows: {
//     deployment_id: string;
//     name: string;
//     neighborhood: string;
//     borough: string;
//     fk_status: string;
//     date_deployed: Date;
//     date_down: Date | null;
//     max_depth_inches: number;
//     current_depth_inches?: number;
//     depth_inches: number[];
//     time: Date[];
//     feature: AugmentedSensorFeature;
// }[]
  const showCurrent = !!currentTime;
  const rows = React.useMemo(() => {
    return features?.map((f: AugmentedSensorFeature) => {
      const id = (f.properties as any)?.id || (f.properties as any)?.deployment_id || f.id;
      return { id, feature: f, ...f.properties };
    });
  }, [features]);
  // console.log('SensorLegendTable features:', rows);

  const columns = useMemo<GridColDef[]>(() => {
    return showCurrent ? [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'max_depth_inches', headerName: 'Max (in)', type: 'number', width: 90, valueGetter: (value: number) => value?.toFixed(1) },
      { field: 'current_depth_inches', headerName: 'Now (in)', type: 'number', width: 90, valueGetter: (value: number) => (value || 0)?.toFixed(1) },
    ] : [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'max_depth_inches', headerName: 'Max (in)', type: 'number', width: 90, valueGetter: (value: number) => value?.toFixed(1) },
    ];
  }, [showCurrent]);

  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    {
      field: showCurrent ? 'current_depth_inches' : 'max_depth_inches',
      sort: 'desc' as 'asc' | 'desc',
    },
  ]);
  useEffect(() => {
    setSortModel([{
      field: showCurrent ? 'current_depth_inches' : 'max_depth_inches',
      sort: 'desc' as 'asc' | 'desc',
    }]);
  }, [showCurrent]);

  // const selectedId = useMemo(() => String(highlightedKey || ''), [highlightedKey]);

  return (
    <Box sx={{ height: 130, width: '100%', pt: 1, overflow: 'auto' }}>
      {
      <DataGrid
        rows={rows}
        columns={columns}
        sx={{ width: '100%' }}
        density="compact"
        getRowId={(row) => row.id}
        rowSelectionModel={{ type: 'include', ids: highlightedKey ? new Set([highlightedKey]) : new Set() }}
        disableRowSelectionOnClick
        hideFooter
        disableColumnMenu
        disableColumnSelector
        filterModel={{
          items: [
            { field: showCurrent ? 'current_depth_inches' : 'max_depth_inches', operator: '>', value: 0 }
          ]
        } as GridFilterModel}
        // rowSelectionModel={selectedId ? [selectedId] : []}
        onRowClick={(params) => onSelect?.((params.row as any).feature)}
        // initialState={{ sorting: { sortModel } }}
        sortModel={sortModel}
        // onSortModelChange={(model) => setSortModel(model)}
        // columnHeaderSortIcon={<></>}
        // columnHeaderFilterIconButton={<></>}
        disableColumnFilter
        disableColumnSorting
        rowHeight={30}
        slots={{
          noResultsOverlay: () => <NoResultsOverlay currentTime={currentTime} />,
        }}
        sx={{
          '&, .MuiDataGrid-withBorderColor': { borderColor: theme.palette.divider },
          '& .MuiDataGrid-columnHeaders': { fontSize: 12, lineHeight: 1, minHeight: 30, maxHeight: 30 },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': { fontSize: 12, lineHeight: 1.2, py: 0.5 },
        }}
      />}
    </Box>
  );
}

const NoResultsOverlay = ({ currentTime }: { currentTime?: Date }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      minWidth: 280,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Box sx={{
        fontSize: 13,
        color: theme.palette.text.secondary,
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}>
        No flooding at {currentTime ? format(currentTime, 'p') : 'at this time'}.
      </Box>
      <Typography variant="caption" sx={{ mt: 1, color: theme.palette.text.secondary, fontSize: 11, textAlign: 'center', px: 2 }}>
        Use the time slider to view past flooding data.
      </Typography>
    </Box>
  );
}

export default React.memo(SensorLegendTable);
